import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import Table from '../common/Table';
import RTSpinner from '../common/RTSpinner';
import { apiGet } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';

const ViewPayments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [filters, setFilters] = useState({ 
    month: new Date().getMonth() + 1, 
    year: new Date().getFullYear(),
    paymentType: '',
    paymentMethod: '',
    paymentProvider: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({ 
    month: new Date().getMonth() + 1, 
    year: new Date().getFullYear(),
    paymentType: '',
    paymentMethod: '',
    paymentProvider: ''
  });
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [totalPayments, setTotalPayments] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalAdjustments, setTotalAdjustments] = useState(0);
  const [totalNetAmount, setTotalNetAmount] = useState(0);

  useEffect(() => {
    const abortController = new AbortController();
    
    const loadInitialData = async () => {
      try {
        // Load payment types and methods first
        await Promise.all([
          loadPaymentTypes(abortController),
          loadPaymentMethods(abortController)
        ]);
        // Then load payment report
        await loadPaymentReport(appliedFilters, abortController);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading initial data:', error);
        }
      }
    };

    loadInitialData();

    return () => {
      abortController.abort();
    };
  }, []);

  const loadPaymentTypes = async (abortController = null) => {
    try {
      const response = await apiGet('/payment-types/', {
        signal: abortController?.signal
      });
      // Handle axios response structure
      const apiResponse = response.data;
      if (apiResponse && apiResponse.success && apiResponse.data) {
        setPaymentTypes(apiResponse.data);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading payment types:', error);
      }
    }
  };

  const loadPaymentMethods = async (abortController = null) => {
    try {
      const response = await apiGet('/payments/methods?status=active', {
        signal: abortController?.signal
      });
      const data = response?.data || response || [];
      const methodsList = Array.isArray(data) ? data : [];
      setPaymentMethods(methodsList);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading payment methods:', error);
      }
    }
  };

  const loadPaymentReport = async (filterParams = appliedFilters, abortController = null) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('limit', '1000');
      
      // Add month/year filters
      if (filterParams.month !== undefined && filterParams.year !== undefined) {
        params.append('month', filterParams.month);
        params.append('year', filterParams.year);
      }

      // Add payment type filter
      if (filterParams.paymentType) {
        params.append('payment_type', filterParams.paymentType);
      }

      // Add payment method filter
      if (filterParams.paymentMethod) {
        params.append('payment_method', filterParams.paymentMethod);
      }

      // Add payment provider filter
      if (filterParams.paymentProvider) {
        params.append('payment_provider', filterParams.paymentProvider);
      }
      
      const response = await apiGet(`/payments/view-payments/?${params.toString()}`, {
        signal: abortController?.signal
      });
      
      // Handle axios response structure - the actual API response is in response.data
      const apiResponse = response.data;
      
      if (apiResponse && apiResponse.success && apiResponse.data) {
        setPayments(apiResponse.data);
        setTotalPayments(apiResponse.total || apiResponse.data.length);
        setTotalAmount(apiResponse.total_amount || 0);
        setTotalAdjustments(apiResponse.total_adjustments || 0);
        setTotalNetAmount(apiResponse.total_net_amount || 0);
        toastSuccess(`Loaded ${apiResponse.data.length} payment records`);
      } else {
        toastError(apiResponse?.message || 'Failed to load payments');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading payments:', error);
        toastError('Error loading payments');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    const abortController = new AbortController();
    setAppliedFilters(filters);
    loadPaymentReport(filters, abortController);
  };

  const resetFilters = () => {
    const defaultFilters = { 
      month: new Date().getMonth() + 1, 
      year: new Date().getFullYear(),
      paymentType: '',
      paymentMethod: '',
      paymentProvider: ''
    };
    const abortController = new AbortController();
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    loadPaymentReport(defaultFilters, abortController);
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const columns = [
    {
      Header: 'Payment Date',
      accessor: 'payment_date',
      Cell: ({ value }) => formatDate(value)
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) => (
        row.pg_id ? (
          <RTButton
            type="link"
            onClick={() => handlePGClick(row.pg_id)}
            className="rt-link-button"
          >
            {value || 'N/A'}
          </RTButton>
        ) : (value || 'N/A')
      )
    },
    {
      Header: 'Invoice Items',
      accessor: 'items_text',
      Cell: ({ value }) => value || 'N/A'
    },
    {
      Header: 'Payment Type',
      accessor: 'payment_type_name',
      Cell: ({ value }) => value || 'N/A'
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      Cell: ({ value }) => formatAmount(value)
    },
    {
      Header: 'Payment Method',
      accessor: 'payment_method',
      Cell: ({ value }) => value || 'N/A'
    },
    {
      Header: 'Payment Provider',
      accessor: 'payment_provider',
      Cell: ({ value }) => value || 'N/A'
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <RTButton
          type="link"
          size="sm"
          onClick={() => navigate(`/payments/${row.id}/view`)}
        >
          View Payment
        </RTButton>
      )
    }
  ];

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear - 5; year <= currentYear + 1; year++) {
    yearOptions.push({ value: year, label: year.toString() });
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">View Payments</h2>
          <RTButton
            variant="outline-primary"
            size="sm"
            onClick={() => navigate('/payments')}
          >
            ← Back
          </RTButton>
        </div>

        {/* Filters Section */}
        <div className="rt-section" style={{ flex: 'none' }}>
          <div className="row mb-3">
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Month</label>
              <select
                className="form-select"
                value={filters.month}
                onChange={(e) => handleFilterChange('month', parseInt(e.target.value))}
              >
                {monthOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Year</label>
              <select
                className="form-select"
                value={filters.year}
                onChange={(e) => handleFilterChange('year', parseInt(e.target.value))}
              >
                {yearOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Payment Type</label>
              <select
                className="form-select"
                value={filters.paymentType}
                onChange={(e) => handleFilterChange('paymentType', e.target.value)}
              >
                <option value="">All Payment Types</option>
                <option value="Payment">Payment</option>
                <option value="Adjustment">Adjustment</option>
                <option value="Advance">Advance</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Payment Method</label>
              <select
                className="form-select"
                value={filters.paymentMethod}
                onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
              >
                <option value="">All Methods</option>
                {paymentMethods.map(method => (
                  <option key={method.id || method.name} value={method.name}>
                    {method.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold" style={{ color: '#153C7F' }}>Payment Provider</label>
              <select
                className="form-select"
                value={filters.paymentProvider}
                onChange={(e) => handleFilterChange('paymentProvider', e.target.value)}
              >
                <option value="">All Providers</option>
                <option value="Manual Entry">Manual Entry</option>
                <option value="Razorpay Gateway">Razorpay</option>
                <option value="PhonePe Gateway">PhonePe</option>
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <RTButton
                variant="primary"
                onClick={applyFilters}
                className="me-2"
                disabled={loading}
              >
                Apply
              </RTButton>
              <RTButton
                variant="outline-secondary"
                onClick={resetFilters}
                disabled={loading}
              >
                Reset
              </RTButton>
            </div>
          </div>
        </div>

        {/* Summary Section */}
        {!loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Payments</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#153C7F' }}>{totalPayments}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Amount</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}>{formatAmount(totalAmount)}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Adjustments</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}>{formatAmount(totalAdjustments)}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #153C7F', borderRight: '4px solid #153C7F', borderRadius: '8px', padding: '0.4rem 0.8rem' }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Amount After Adjustments</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}>{formatAmount(totalNetAmount)}</div>
            </div>
          </div>
        )}

        {/* Table Section */}
        <div className="rt-section">
          {loading ? (
            <div className="text-center">
              <RTSpinner message='Loading payments...' />
            </div>
          ) : (
            <Table
              columns={columns}
              data={payments}
              emptyMessage="No payments found for the selected criteria"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewPayments;
